import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { StrategyModalProvider } from "@/lib/useStrategyModal";
import ScrollToTop from "@/components/ScrollToTop";
import PixelRouteTracker from "@/components/PixelRouteTracker";
import { HelmetProvider } from "react-helmet-async";

/* ---------- Route-level code splitting ---------- */
/* Home stays eager (it's the LCP route most visitors hit) but every other
   route is lazy-loaded so we don't ship 16 industry pages on first paint. */
const Home = lazy(() => import("@/pages/Home"));
const Legal = lazy(() => import("@/pages/Legal"));
const CaseStudies = lazy(() => import("@/pages/CaseStudies"));
const CaseStudyDetail = lazy(() => import("@/pages/CaseStudyDetail"));
const Healthcare = lazy(() => import("@/pages/industries/Healthcare"));
const D2C = lazy(() => import("@/pages/industries/D2C"));
const SaaS = lazy(() => import("@/pages/industries/SaaS"));
const RealEstate = lazy(() => import("@/pages/industries/RealEstate"));
const Fintech = lazy(() => import("@/pages/industries/Fintech"));
const Education = lazy(() => import("@/pages/industries/Education"));
const Luxury = lazy(() => import("@/pages/industries/Luxury"));
const FnBHospitality = lazy(() => import("@/pages/industries/FnBHospitality"));
const FashionApparel = lazy(() => import("@/pages/industries/FashionApparel"));
const BeautyWellness = lazy(() => import("@/pages/industries/BeautyWellness"));
const TravelExperiences = lazy(() => import("@/pages/industries/TravelExperiences"));
const Automotive = lazy(() => import("@/pages/industries/Automotive"));
const LegalProfessional = lazy(() => import("@/pages/industries/LegalProfessional"));
const PersonalCreators = lazy(() => import("@/pages/industries/PersonalCreators"));
const AstrologySpiritual = lazy(() => import("@/pages/industries/AstrologySpiritual"));
const CoachingEdtech = lazy(() => import("@/pages/industries/CoachingEdtech"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));

/** Aggressively remove platform-injected badges, even if reinjected later. */
function useKillEmergentBadge() {
  useEffect(() => {
    const kill = () => {
      const sels = [
        "#emergent-badge",
        'a[id="emergent-badge"]',
        'a[href*="emergent-badge"]',
        'a[href*="utm_source=emergent-badge"]',
      ];
      sels.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => el.remove());
      });
    };
    kill();
    const mo = new MutationObserver(kill);
    mo.observe(document.documentElement, { childList: true, subtree: true });
    const iv = setInterval(kill, 500);
    return () => {
      mo.disconnect();
      clearInterval(iv);
    };
  }, []);
}

/** Lightweight placeholder while a route chunk loads. */
function RouteFallback() {
  return (
    <div
      aria-hidden
      style={{
        minHeight: "100vh",
        background: "#F2F5F1",
      }}
    />
  );
}

function App() {
  useKillEmergentBadge();
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <StrategyModalProvider>
            <ScrollToTop />
            <PixelRouteTracker />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/case-studies" element={<CaseStudies />} />
                <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
                <Route path="/industries/healthcare" element={<Healthcare />} />
                <Route path="/industries/d2c" element={<D2C />} />
                <Route path="/industries/saas" element={<SaaS />} />
                <Route path="/industries/real-estate" element={<RealEstate />} />
                <Route path="/industries/fintech" element={<Fintech />} />
                <Route path="/industries/education" element={<Education />} />
                <Route path="/industries/luxury-lifestyle" element={<Luxury />} />
                <Route path="/industries/fnb-hospitality" element={<FnBHospitality />} />
                <Route path="/industries/fashion-apparel" element={<FashionApparel />} />
                <Route path="/industries/beauty-wellness" element={<BeautyWellness />} />
                <Route path="/industries/travel-experiences" element={<TravelExperiences />} />
                <Route path="/industries/automotive" element={<Automotive />} />
                <Route path="/industries/legal-professional" element={<LegalProfessional />} />
                <Route path="/industries/personal-creators" element={<PersonalCreators />} />
                <Route path="/industries/astrology-spiritual" element={<AstrologySpiritual />} />
                <Route path="/industries/coaching-edtech" element={<CoachingEdtech />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/legal/:slug" element={<Legal />} />
              </Routes>
            </Suspense>
            <Toaster richColors position="top-center" />
          </StrategyModalProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
